import * as z from 'zod';
import type { TimeData } from '../../core/TimeData';
import {
  type CoaEvent,
  type EventOrLeg,
  type Events,
  isCargoEvent,
  isOffHireEventTimeData,
  isTimeData,
  type Leg,
  type OffHireEvent,
  type SpotEvent,
  type TCOrderEvent,
  type WaitEvent,
} from '../../core/TimeData';
import type { ClientId } from '@seaber/common/lib/clientId';
import type { ScenarioOrder } from '../../../lib/planningData';
import { DefaultMap } from '@seaber/common/lib/collections';
import { EventCategory } from '@seaber/common/core/entries';
import { formatDays } from '../../../lib/util';
import { dToH } from '../../../lib/time';
import { brandedZodNumber } from '../../../lib/zod-util';
import type { OrderId } from '@seaber/common/graphql/types';
import type { ScenarioSchedule } from '@seaber/common/core/scenario';
import {
  ItemType,
  type ScenarioTimelineGroup,
  type ScenarioTimelineGroupFields,
  type ScenarioTimelineItem,
  type TimelineSuggestion,
  type TimelineVoyage,
} from '../types';
import type { TimelineItem } from '../react-calendar-timeline';

// TODO rename groupOrdersByPortCallId or maybe remove completely if getRelatedOrder is no longer used https://github.com/Seaber-io/frontend/pull/626#discussion_r705071343
export const groupOrdersByPortCallId = (
  timedata: TimeData[],
): Map<ClientId, ScenarioOrder[]> => {
  const ordersByPortCallId = new DefaultMap<ClientId, ScenarioOrder[]>(
    () => [],
  );
  for (const { event: e } of timedata) {
    if (isCargoEvent(e) && e.portCallId !== null) {
      ordersByPortCallId.get(e.portCallId).push(e.order);
    }
  }
  return ordersByPortCallId;
};

// TODO: getRelatedOrder in ScenarioTimeline seems odd given current data structures. could perhaps be dropped or at least written in a more obvious way to whatever its purpose was https://github.com/Seaber-io/frontend/pull/626#discussion_r703433733
export const getRelatedOrder = (
  ordersByPortCallId: Map<ClientId, ScenarioOrder[]>,
  e: Events,
): ScenarioOrder | null =>
  'order' in e
    ? e.order
    : ('portCallId' in e &&
        e.portCallId != null &&
        ordersByPortCallId.get(e.portCallId)?.[0]) ||
      null;
export const freeEventFilter = (
  e: TimeData,
): e is TimeData<
  Exclude<Events, TCOrderEvent | SpotEvent | CoaEvent | OffHireEvent>
> => {
  const { category } = e.event;
  return (
    category !== EventCategory.TC &&
    category !== EventCategory.Spot &&
    category !== EventCategory.COA &&
    category !== EventCategory.OffHire &&
    category !== EventCategory.Static
  );
};
export const isLegOrWait = (e: EventOrLeg): e is Leg | WaitEvent =>
  'category' in e;

/**
 * Assigns non-overlapping free events to the next shipment they precede.
 * Returns a map of shipment index -> array of events that should be included in that shipment.
 *
 * Logic:
 * - Events that overlap with shipments are handled by existing getOverlappingEvents logic
 * - Events that don't overlap with ANY shipment should be assigned to the NEXT shipment
 * - OffHire events are excluded and remain standalone
 */
export function assignNonOverlappingEventsToNextShipment(
  sortedShipments: Array<{
    start: number;
    end: number;
    portCallIds: Set<ClientId>;
  }>,
  freeEvents: EventOrLeg[],
): Map<number, EventOrLeg[]> {
  function addEventToShipment(shipmentIndex: number, event: EventOrLeg) {
    eventsByShipmentIndex.get(shipmentIndex).push(event);
  }

  const eventsByShipmentIndex = new DefaultMap<number, EventOrLeg[]>(() => []);

  if (sortedShipments.length === 0) {
    return eventsByShipmentIndex;
  }

  const assignedPortCallIds = new Set<ClientId>();
  sortedShipments.forEach(({ portCallIds }) => {
    portCallIds.forEach((portCallId) => {
      assignedPortCallIds.add(portCallId);
    });
  });

  // Collect keys of events that overlap with any shipment by time range.
  // These are handled by getOverlappingEvents instead.
  const overlappingEventKeys = new Set<string>();
  for (const event of freeEvents) {
    for (const shipment of sortedShipments) {
      if (event.start < shipment.end && event.end > shipment.start) {
        overlappingEventKeys.add(event.key);
        break;
      }
    }
  }

  // Filter out OffHire events upfront and sort by end time for efficient processing
  const sortedFreeEvents = freeEvents
    .filter((event) => {
      if (isTimeData(event) && isOffHireEventTimeData(event)) {
        return false;
      }
      if (
        event.event.portCallId &&
        assignedPortCallIds.has(event.event.portCallId)
      ) {
        return false;
      }
      if (overlappingEventKeys.has(event.key)) {
        return false;
      }
      return true;
    })
    .sort((a, b) => a.end - b.end);

  let currentShipmentIndex = 0;
  for (const freeEvent of sortedFreeEvents) {
    let currentShipment = sortedShipments[currentShipmentIndex];

    if (!currentShipment) {
      break;
    }

    const portCallId = freeEvent.event.portCallId;
    if (!portCallId) {
      continue;
    }

    const isPortCallIncludedInShipment =
      currentShipment.portCallIds.has(portCallId);

    if (isPortCallIncludedInShipment) {
      continue;
    }

    while (currentShipment && currentShipment.start < freeEvent.end) {
      currentShipmentIndex++;
      currentShipment = sortedShipments[currentShipmentIndex];
    }

    if (!currentShipment) {
      break;
    }

    if (currentShipment.start >= freeEvent.end) {
      addEventToShipment(currentShipmentIndex, freeEvent);
    }
  }

  return eventsByShipmentIndex;
}

export const dropOrdersToTimelineSchema = z.object({
  type: z.enum(['orders']),
  selectedOrderIds: z.array(brandedZodNumber<OrderId>()),
  dragOrderId: brandedZodNumber<OrderId>().nullable(),
});
const dnDShipmentsOnTimelineSchema = z.object({
  type: z.enum(['shipments']),
  orderIds: z.array(brandedZodNumber<OrderId>()),
  singleEventIds: z.array(z.string()),
  isWholeShipment: z.boolean(),
});
const dnDSingleEventOnTimelineSchema = z.object({
  type: z.enum(['single_event']),
  singleEventId: z.string(),
});
export const dragDropDataSchema = z.union([
  dropOrdersToTimelineSchema,
  dnDShipmentsOnTimelineSchema,
  dnDSingleEventOnTimelineSchema,
]);

export function snapTimeTo1HGrid(time: number | Date): Date {
  const date = new Date(time);
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    0,
    0,
    // This is a small workaround to give space for ETA and ETB timestamps.
    // They are 2 and 4 ms behind loadingStart timestamp
    // Without this ETA and ETB will have time with minutes=59
    50,
  );
}

/** Copies of voyages from other contracts, shown greyed out on vessel rows sharing the vessel. */
export const getShadowItems = ({
  items,
  filteredGroups,
  spotAssignments,
}: {
  items: Array<ScenarioTimelineItem | TimelineItem<TimelineSuggestion>>;
  filteredGroups: ScenarioTimelineGroup[];
  spotAssignments: ScenarioSchedule['spotAssignments'];
}): ScenarioTimelineItem[] =>
  items
    .filter(
      (item: ScenarioTimelineItem) =>
        (item.type === ItemType.COA && item.groupObj.timeline.vesselId) ||
        item.type === ItemType.SPOT,
    )
    .flatMap((x: TimelineItem<TimelineVoyage>) => {
      const voyage = x.groupObj.timeline;
      const spotVesselId =
        voyage.type === 'spot' && 'orders' in x
          ? spotAssignments.find((s) => s.orderId === x.orders[0].id)?.vesselId
          : undefined;

      /** Another contract's row for the same vessel and shipowner. */
      const isShadowTarget = (row: ScenarioTimelineGroupFields['timeline']) => {
        if (voyage.type === 'coa') {
          if (row.vesselId !== voyage.vesselId) return false;
          return row.type === 'coa'
            ? row.coaId !== voyage.coaId
            : row.type === 'spot' &&
                row.ownerOrganizationId === voyage.coa?.shipowner?.id;
        }
        return (
          voyage.type === 'spot' &&
          row.coa?.shipowner?.id === voyage.ownerOrganizationId &&
          row.vesselId != null &&
          row.vesselId === spotVesselId
        );
      };

      return filteredGroups
        .filter((group) => isShadowTarget(group.timeline))
        .map((group) => ({
          ...x,
          id: `inactive-${group.id}-item-${x.id}`,
          originalItemId: x.id,
          originalContract: voyage.coa?.name ?? '',
          isShadow: true,
          group: group.id,
          groupObj: { ...group },
        }));
    });

/**
 * Given a number of days, returns a formatted total voyage duration in days and hours
 * e.g "3.4 days (81 h)"
 * If < 1 day, returns only hours, e.g. "23 h"
 * @param totalDays number of days
 */
export function formatTotalVoyageDuration(totalDays: number): string {
  if (totalDays < 1) {
    return `${Math.round(dToH(totalDays))} h`;
  } else {
    return `${formatDays(totalDays)} (${Math.round(dToH(totalDays))} h)`;
  }
}
